import { Button, HSpacer, Text, VSpacer } from '@/components/DesignSystem';
import { Avatar } from '@/components/DesignSystem/Avatar/Avatar';
import { PointsRemainingChip } from '@/components/shared/PointsRemainingChip';
import {
  SubwalletProductsBottomSheet,
} from '@/components/shared/SubwalletBalance/SubwalletProductsBottomSheet';
import { AppConfig } from '@/constants/AppConfig';
import { QueryKeys } from '@/constants/QueryKeys';
import { SubwalletApi } from '@/utilities/api/SubwalletApi';
import { ApiSubwallet, ApiSubwalletBalance } from '@api/interfaces';
import KeyboardArrowUp from '@mui/icons-material/KeyboardArrowUp';
import KeyboardArrowDown from '@mui/icons-material/KeyboardArrowDown';
import { Stack } from '@mui/material';
import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useQuery } from 'react-query';

interface ReservedPointsBalanceCardProps {
  accessToken?: string,
  balance: ApiSubwalletBalance,
  brandColor?: string,
}

export const ReservedPointsBalanceCard = ({
  accessToken,
  balance,
  brandColor,
}: ReservedPointsBalanceCardProps) => {
  const { t } = useTranslation();
  const [expanded, setExpanded] = useState(false);
  const [showRedeemableProducts, setShowRedeemableProducts] = useState(false);
  const getLogo = (subwallet: ApiSubwallet) => {
    return subwallet.manufacturer?.logo || subwallet.retailer?.logomark;
  };

  const {
    data: redeemableProducts,
  } = useQuery(
    [QueryKeys.LIST_REDEEMABLE_PRODUCTS, balance.subwalletId],
    async () => SubwalletApi.listRedeemableProducts(
      balance.subwalletId,
      { accessToken, limit: 1 },
    ),
  );
  const hasRedeemableProducts = (redeemableProducts?.total ?? 0) > 0;

  return (
    <Stack
      border="1px solid rgba(0, 0, 0, 0.12)"
      borderRadius="8px"
      p="16px 12px 14px 12px"
      sx={{ cursor: 'pointer' }}
    >
      <Stack direction="row" justifyContent="space-between" onClick={() => setExpanded(!expanded)}>
        <Stack direction="row">
          <Avatar
            alt={`${balance.subwallet?.name} logo`}
            defaultIcon={balance.manufacturerId ? 'manufacturer' : 'retailer'}
            src={balance.subwallet && getLogo(balance.subwallet)
              ? `${AppConfig.staticImageHost}/${getLogo(balance.subwallet)}`
              : undefined
            }
            sx={{ height: '40px', width: '40px' }}
          />
          <HSpacer size="4" />
          <Stack>
            <Text category="overline">
              {t($ => $.bookings.reservedPoints.label)}
            </Text>
            <Text category="body-xlarge">
              {balance.subwallet?.name}
            </Text>
            <VSpacer size="1" />
            <PointsRemainingChip
              text={t(
                $ => $.common.booking.pointsRemaining,
                { count: balance.points },
              )}
            />
          </Stack>
        </Stack>
        {expanded ? <KeyboardArrowUp /> : <KeyboardArrowDown />}
      </Stack>
      {expanded && (
        <>
          <VSpacer size="4" />
          <VSpacer size="1" />
          <Text category="body-medium">
            {balance.subwallet?.redeemableProductsDescription}
          </Text>
          {hasRedeemableProducts && (
            <>
              <VSpacer size="5" />
              <Button
                onClick={() => {
                  setShowRedeemableProducts(true);
                }}
                sx={{
                  bgcolor: brandColor,
                  borderRadius: '3px',
                  color: 'white',
                  '&:hover': { bgcolor: brandColor },
                }}
                testID="view-redeemable-products-button"
                variant="contained"
              >
                {t($ => $.bookings.reservedPoints.viewRedeemable)}
              </Button>
            </>
          )}
        </>
      )}
      {showRedeemableProducts && (
        <SubwalletProductsBottomSheet
          accessToken={accessToken}
          brandColor={brandColor}
          onClose={() => setShowRedeemableProducts(false)}
          subwalletId={balance.subwalletId}
        />
      )}
    </Stack>
  );
};
